import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getContactInitials } from '../utils/contactInitials';
import { contactMatchesQuery, getContactSociete } from '../utils/contactIdentity';
import { isContactAcquireur, isContactMandant } from '../utils/contactProfile';
import { formatPhoneFR, formatDateFR } from '../utils/formatFR';
import { getMediaUrl } from '../utils/mediaUrl';
import api from '../services/api';
import contactsService from '../services/contactsService';
import ContactCascadeModal from '../components/contacts/ContactCascadeModal';
import ContactBulkEmailModal from '../components/contacts/ContactBulkEmailModal';
import ContactBulkArchiveModal from '../components/contacts/ContactBulkArchiveModal';
import ContactListMobile from '../components/contacts/ContactListMobile';
import { useTelephone } from '../hooks/useTelephone';

const ContactList = ({ 
  contacts = [],
  loading = false,
  selectedContacts = [],
  setSelectedContacts,
  startNewContact,
  editContact,
  viewContact,
  deleteContact,
  showArchivedContacts = false,
  setShowArchivedContacts,
  loadContacts,
  contactSearchFilters = {},
  setContactSearchFilters,
  contactTypes = [],
  contactGroupes = [],
  bulkArchiveContacts,
  bulkUnarchiveContacts,
  bulkDeleteContacts,
  handleContactSelection,
  handleSelectAllContacts,
  // AJ3 — false pendant le second chargement : la recherche ne porte encore que sur la
  // première page, il faut le dire plutôt que de laisser croire à un carnet complet.
  chargementComplet = true,
}) => {
  // lot AE3 — le lien « Voir les N contacts » de la recherche du header arrive sur
  // /contacts?q=… : on lit le paramètre au montage et à chaque navigation (même modèle que
  // PropertiesListPage, qui utilise location.key).
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(
    () => new URLSearchParams(window.location.search).get('q') || '',
  );
  const [filterType, setFilterType] = useState('tous');
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q !== null) setSearchQuery(q);
  }, [location.key]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    budget: 'tous',
    ville: 'tous',
    statut: 'tous',
    activite: 'tous'
  });
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [archivedCount, setArchivedCount] = useState(0);
  // lot DD — rendu téléphone : « Afficher plus » (+ 20) remplace la pagination.
  const telephone = useTelephone();
  const navigate = useNavigate();
  const [nombreAffiche, setNombreAffiche] = useState(20);
  useEffect(() => { setNombreAffiche(20); }, [searchQuery, filterType, showArchivedContacts]);
  const [activeContactsCounts, setActiveContactsCounts] = useState({ tous: 0, acquereurs: 0, proprietaires: 0 });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [cascadeModal, setCascadeModal] = useState({ open: false, mode: 'archive', contact: null, linkedProperties: [], hasActiveSearches: false, isAcquereur: false, isProprietaire: false });
  const [actionBusyId, setActionBusyId] = useState(null);

  // Récupère les biens liés (propriétaire) + flags rôle avant archive/suppression
  const fetchLinks = async (contact) => {
    const { data } = await api.get(`/contacts/${contact.id}/linked-properties`);
    return data;
  };

  // Archiver : si biens actifs liés → modale cascade, sinon archive directe
  const handleArchiveClick = async (contact) => {
    setActiveDropdown(null);
    setActionBusyId(contact.id);
    try {
      const links = await fetchLinks(contact);
      if (links.count > 0) {
        setCascadeModal({
          open: true,
          mode: 'archive',
          contact,
          linkedProperties: links.linked_properties,
          hasActiveSearches: links.has_active_searches,
          isAcquereur: links.is_acquereur,
          isProprietaire: links.is_proprietaire,
        });
      } else {
        if (window.confirm(`Archiver ${contact.prenom || ''} ${contact.nom} ?`)) {
          await api.post(`/contacts/${contact.id}/archive`, {});
          loadContacts ? loadContacts() : window.location.reload();
        }
      }
    } catch (e) {
      console.error('Erreur archivage:', e);
      alert("Erreur lors de l'archivage du contact");
    } finally {
      setActionBusyId(null);
    }
  };

  // Supprimer : si biens actifs liés → modale blocage (409), sinon suppression
  const handleDeleteClick = async (contact) => {
    setActiveDropdown(null);
    setActionBusyId(contact.id);
    try {
      const links = await fetchLinks(contact);
      if (links.count > 0) {
        setCascadeModal({
          open: true,
          mode: 'delete-blocked',
          contact,
          linkedProperties: links.linked_properties,
          hasActiveSearches: links.has_active_searches,
          isAcquereur: links.is_acquereur,
          isProprietaire: links.is_proprietaire,
        });
      } else if (window.confirm(`Supprimer définitivement ${contact.prenom || ''} ${contact.nom} ?`)) {
        if (deleteContact) deleteContact(contact.id);
      }
    } catch (e) {
      console.error('Erreur suppression:', e);
      alert('Erreur lors de la suppression du contact');
    } finally {
      setActionBusyId(null);
    }
  };

  // Confirmation d'archivage cascade depuis la modale
  const confirmCascadeArchive = async (propertyIds) => {
    const contact = cascadeModal.contact;
    if (!contact) return;
    try {
      await api.post(`/contacts/${contact.id}/archive`, { cascade_property_ids: propertyIds });
      setCascadeModal({ open: false, mode: 'archive', contact: null, linkedProperties: [], hasActiveSearches: false, isAcquereur: false, isProprietaire: false });
      loadContacts ? loadContacts() : window.location.reload();
    } catch (e) {
      console.error('Erreur archivage cascade:', e);
      alert("Erreur lors de l'archivage en cascade");
    }
  };

  // Fermer le menu ⋮ au clic en dehors
  useEffect(() => {
    if (activeDropdown === null) return undefined;
    const close = () => setActiveDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [activeDropdown]);

  // Filtrer les contacts
  const filteredContacts = contacts.filter(contact => {
    // iter114 — recherche unifiée : nom, prénom, SOCIÉTÉ (nom_societe/societe/raison_sociale/
    // enseigne), référence, ville, tous les emails, tous les téléphones (formats FR/E.164),
    // insensible à la casse ET aux accents.
    if (searchQuery && !contactMatchesQuery(contact, searchQuery)) return false;

    if (filterType !== 'tous') {
      if (filterType === 'acquéreurs') {
        if (!isContactAcquireur(contact)) return false;
      } else if (filterType === 'propriétaires') {
        if (!isContactMandant(contact)) return false;
      } else {
        const hasGroup = contact.groupes?.some(g => 
          g.toLowerCase() === filterType.toLowerCase()
        );
        if (!hasGroup) return false;
      }
    }

    if (filters.ville !== 'tous' && contact.ville !== filters.ville) return false;
    if (filters.statut !== 'tous' && contact.statut_lead !== filters.statut) return false;

    return true;
  });

  // Trier les contacts
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    let aVal, bVal;
    
    if (sortField === 'nom') {
      aVal = `${a.prenom || ''} ${a.nom || ''}`.toLowerCase();
      bVal = `${b.prenom || ''} ${b.nom || ''}`.toLowerCase();
    } else if (sortField === 'ville') {
      aVal = (a.ville || '').toLowerCase();
      bVal = (b.ville || '').toLowerCase();
    } else if (sortField === 'created_at') {
      aVal = new Date(a.created_at);
      bVal = new Date(b.created_at);
    } else {
      return 0;
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedContacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContacts = sortedContacts.slice(startIndex, startIndex + itemsPerPage);

  // Gestion du tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Gestion sélection
  const toggleContactSelection = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === paginatedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(paginatedContacts.map(c => c.id));
    }
  };

  // Helper functions
  const getInitials = (contact) => getContactInitials(contact.prenom, contact.nom);

  const getAvatarGradient = (contact) => {
    const gradients = [
      'bg-gradient-to-br from-blue-500 to-blue-600',
      'bg-gradient-to-br from-pink-500 to-pink-600',
      'bg-gradient-to-br from-purple-500 to-purple-600',
      'bg-gradient-to-br from-orange-500 to-orange-600',
      'bg-gradient-to-br from-teal-500 to-teal-600',
      'bg-gradient-to-br from-indigo-500 to-indigo-600'
    ];
    const index = (contact.nom?.charCodeAt(0) || 0) % gradients.length;
    return gradients[index];
  };

  const countByType = (type) => {
    if (!showArchivedContacts) {
      if (type === 'tous') return contacts.length;
      if (type === 'acquéreurs') return contacts.filter(isContactAcquireur).length;
      if (type === 'propriétaires') return contacts.filter(isContactMandant).length;
      return 0;
    } else {
      if (type === 'tous') return activeContactsCounts.tous;
      if (type === 'acquéreurs') return activeContactsCounts.acquereurs;
      if (type === 'propriétaires') return activeContactsCounts.proprietaires;
      return 0;
    }
  };

  // AJ3 — ces compteurs déclenchaient DEUX chargements complets (actifs + archivés) à chaque
  // changement de `contacts`, donc à nouveau après le second temps de chargement : quatre
  // téléchargements du carnet pour afficher trois nombres. Ils sont maintenant DÉDUITS de la
  // liste déjà en mémoire (aucune requête), et le nombre d'archivés vient d'un compteur.
  React.useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const nombre = await contactsService.getContactsCount('archived');
        if (!annule) setArchivedCount(nombre);
      } catch (error) {
        console.error('Error fetching archived count:', error);
      }
    })();
    return () => { annule = true; };
  }, [showArchivedContacts]);

  React.useEffect(() => {
    // On n'actualise qu'avec un carnet complet : sinon les onglets afficheraient les
    // compteurs de la seule première page pendant une seconde.
    if (showArchivedContacts || !chargementComplet) return;
    const actifs = contacts || [];
    setActiveContactsCounts({
      tous: actifs.length,
      acquereurs: actifs.filter(isContactAcquireur).length,
      proprietaires: actifs.filter(isContactMandant).length,
    });
  }, [contacts, chargementComplet, showArchivedContacts]);

  // Actions groupées
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkPrinting, setBulkPrinting] = useState(false);
  const [bulkArchiveModal, setBulkArchiveModal] = useState({ open: false, items: [] });
  const [bulkArchiveBusy, setBulkArchiveBusy] = useState(false);

  // Archivage groupé : vérifie les biens liés (cascade) avant d'archiver
  const handleBulkArchiveClick = async () => {
    if (selectedContacts.length === 0) {
      alert('⚠️ Veuillez sélectionner au moins un contact');
      return;
    }
    try {
      const results = await Promise.all(
        selectedContacts.map(async (id) => {
          const { data } = await api.get(`/contacts/${id}/linked-properties`);
          const c = contacts.find((x) => x.id === id) || { id };
          return { contact: { id, prenom: c.prenom, nom: c.nom }, properties: data.linked_properties || [] };
        })
      );
      const withBiens = results.filter((r) => r.properties.length > 0);
      if (withBiens.length === 0) {
        if (window.confirm(`Archiver ${selectedContacts.length} contact(s) ?`)) {
          await Promise.all(selectedContacts.map((id) => api.post(`/contacts/${id}/archive`, {})));
          setSelectedContacts([]);
          loadContacts ? loadContacts() : window.location.reload();
        }
      } else {
        setBulkArchiveModal({ open: true, items: withBiens });
      }
    } catch (e) {
      console.error('Erreur archivage groupé:', e);
      alert('Erreur lors de la vérification des biens liés');
    }
  };

  const runBulkArchive = async (cascade) => {
    setBulkArchiveBusy(true);
    try {
      const itemsById = {};
      bulkArchiveModal.items.forEach((it) => { itemsById[it.contact.id] = it.properties.map((p) => p.id); });
      await Promise.all(
        selectedContacts.map((id) => {
          const cascadeIds = cascade ? (itemsById[id] || []) : [];
          return api.post(`/contacts/${id}/archive`, { cascade_property_ids: cascadeIds });
        })
      );
      setBulkArchiveModal({ open: false, items: [] });
      setSelectedContacts([]);
      loadContacts ? loadContacts() : window.location.reload();
    } catch (e) {
      console.error('Erreur archivage groupé cascade:', e);
      alert("Erreur lors de l'archivage groupé");
    } finally {
      setBulkArchiveBusy(false);
    }
  };

  const handleBulkEmail = () => {
    if (selectedContacts.length === 0) {
      alert('⚠️ Veuillez sélectionner au moins un contact');
      return;
    }
    setShowBulkEmail(true);
  };

  const handleBulkPrint = async () => {
    if (selectedContacts.length === 0) {
      alert('⚠️ Veuillez sélectionner au moins un contact');
      return;
    }
    setBulkPrinting(true);
    try {
      const res = await api.post(
        `/contacts/bulk-fiche-contact?version=agent`,
        { contact_ids: selectedContacts },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiches_contacts_${selectedContacts.length}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erreur impression groupée:', e);
      alert('Erreur lors de la génération du PDF groupé');
    } finally {
      setBulkPrinting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <i className="bi bi-arrow-clockwise animate-spin text-4xl text-green-600 mb-4 block"></i>
          <p className="text-slate-600">Chargement des contacts...</p>
        </div>
      </div>
    );
  }

  // lot DD — téléphone : un SEUL rendu est monté (la logique de filtrage et de tri reste ici).
  if (telephone) {
    const filtresActifs = Object.values(filters).filter((v) => v !== 'tous').length;
    return (
      <>
        <ContactListMobile
          contacts={sortedContacts.slice(0, nombreAffiche)}
          total={sortedContacts.length}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          filterType={filterType}
          onFilterType={setFilterType}
          showArchived={showArchivedContacts}
          onShowArchived={(v) => setShowArchivedContacts && setShowArchivedContacts(v)}
          countByType={countByType}
          archivedCount={archivedCount}
          onOpenContact={(c) => viewContact && viewContact(c)}
          onNew={() => navigate('/contacts/express')}
          onOpenFilters={() => setShowAdvancedFilters(true)}
          filtresActifs={filtresActifs}
          affichables={nombreAffiche}
          onAfficherPlus={() => setNombreAffiche((n) => n + 20)}
        />
        {showAdvancedFilters && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-end" data-testid="contact-mobile-filters-sheet">
            <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-y-auto p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-slate-900">Filtres et tri</h2>
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(false)}
                  data-testid="contact-mobile-filters-close"
                  className="w-11 h-11 rounded-lg text-slate-500 flex items-center justify-center"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor="tri-mobile">Tri</label>
                <select
                  id="tri-mobile"
                  value={`${sortField}:${sortOrder}`}
                  onChange={(e) => {
                    const [champ, ordre] = e.target.value.split(':');
                    setSortField(champ);
                    setSortOrder(ordre);
                  }}
                  data-testid="contact-mobile-sort"
                  className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg"
                >
                  <option value="updated_at:desc">Modifiés récemment</option>
                  <option value="nom:asc">Nom A → Z</option>
                  <option value="created_at:desc">Créés récemment</option>
                </select>
              </div>

              {[
                { cle: 'ville', label: 'Ville',
                  options: [...new Set(contacts.map((c) => c.ville).filter(Boolean))].sort()
                    .map((v) => [v, v]) },
                { cle: 'statut', label: 'Statut',
                  options: [['Nouveau', 'Nouveau'], ['Qualifié', 'Qualifié'],
                    ['En négociation', 'En négociation'], ['Converti', 'Converti'],
                    ['Perdu', 'Perdu']] },
                { cle: 'budget', label: 'Budget',
                  options: [['<100k', 'Moins de 100 000 €'], ['100-300k', '100 000 € — 300 000 €'],
                    ['300-500k', '300 000 € — 500 000 €'], ['>500k', 'Plus de 500 000 €']] },
              ].map((f) => (
                <div key={f.cle}>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5" htmlFor={`filtre-${f.cle}`}>
                    {f.label}
                  </label>
                  <select
                    id={`filtre-${f.cle}`}
                    value={filters[f.cle]}
                    onChange={(e) => setFilters({ ...filters, [f.cle]: e.target.value })}
                    data-testid={`contact-mobile-filter-${f.cle}`}
                    className="w-full h-11 px-3 text-base border border-slate-300 rounded-lg"
                  >
                    <option value="tous">Tous</option>
                    {f.options.map(([valeur, libelle]) => (
                      <option key={valeur} value={valeur}>{libelle}</option>
                    ))}
                  </select>
                </div>
              ))}

              <button
                type="button"
                onClick={() => setShowAdvancedFilters(false)}
                data-testid="contact-mobile-filters-apply"
                className="w-full h-12 rounded-xl bg-blue-600 text-white font-bold"
              >
                Voir les résultats
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
            <i className="bi bi-people-fill text-green-600 text-xl sm:text-2xl"></i>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Contacts</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Gérez votre base de contacts clients et prospects</p>
          </div>
        </div>
        <button
          onClick={startNewContact}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg text-sm font-bold shadow-md shadow-emerald-100 transition-all"
        >
          <i className="bi bi-person-plus-fill"></i>
          <span className="hidden sm:inline">Nouveau contact</span>
          <span className="sm:hidden">Nouveau</span>
        </button>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-4">
        {/* Ligne 1 : Recherche + Filtres types + Tri */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
          {/* Recherche */}
          <div className="flex-1 relative">
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Recherche dynamique (nom, société, email, téléphone)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="contacts-search-input"
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Filtres par type */}
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={() => {
                setFilterType('tous');
                if (showArchivedContacts) {
                  setShowArchivedContacts && setShowArchivedContacts(false);
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                filterType === 'tous' && !showArchivedContacts
                  ? 'bg-green-500 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Tous <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                filterType === 'tous' && !showArchivedContacts ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}>{countByType('tous')}</span>
            </button>
            <button 
              onClick={() => {
                setFilterType('acquéreurs');
                if (showArchivedContacts) {
                  setShowArchivedContacts && setShowArchivedContacts(false);
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                filterType === 'acquéreurs' && !showArchivedContacts
                  ? 'bg-green-500 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Acquéreurs <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                filterType === 'acquéreurs' && !showArchivedContacts ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}>{countByType('acquéreurs')}</span>
            </button>
            <button 
              onClick={() => {
                setFilterType('propriétaires');
                if (showArchivedContacts) {
                  setShowArchivedContacts && setShowArchivedContacts(false);
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                filterType === 'propriétaires' && !showArchivedContacts
                  ? 'bg-green-500 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              Mandants <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                filterType === 'propriétaires' && !showArchivedContacts ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}>{countByType('propriétaires')}</span>
            </button>
            <button 
              onClick={() => {
                setShowArchivedContacts && setShowArchivedContacts(true);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                showArchivedContacts
                  ? 'bg-amber-500 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className="bi bi-archive mr-1"></i>
              Archivés <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                showArchivedContacts ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}>{archivedCount}</span>
            </button>
          </div>

          {/* Filtres avancés */}
          <button 
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition flex items-center gap-2"
          >
            <i className="bi bi-funnel"></i>
            Filtres avancés
          </button>
        </div>

        {/* Filtres avancés - Panneau */}
        {showAdvancedFilters && (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Ville</label>
                <select
                  value={filters.ville}
                  onChange={(e) => setFilters({...filters, ville: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="tous">Toutes les villes</option>
                  {[...new Set(contacts.map(c => c.ville).filter(Boolean))].sort().map(ville => (
                    <option key={ville} value={ville}>{ville}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Statut</label>
                <select
                  value={filters.statut}
                  onChange={(e) => setFilters({...filters, statut: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="tous">Tous les statuts</option>
                  <option value="Nouveau">Nouveau</option>
                  <option value="Qualifié">Qualifié</option>
                  <option value="En négociation">En négociation</option>
                  <option value="Converti">Converti</option>
                  <option value="Perdu">Perdu</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Budget</label>
                <select
                  value={filters.budget}
                  onChange={(e) => setFilters({...filters, budget: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="tous">Tous les budgets</option>
                  <option value="<100k">Moins de 100 000 €</option>
                  <option value="100-300k">100 000 € — 300 000 €</option>
                  <option value="300-500k">300 000 € — 500 000 €</option>
                  <option value=">500k">Plus de 500 000 €</option>
                </select>
              </div>
              <div className="flex items-end">
                <button 
                  onClick={() => setFilters({ budget: 'tous', ville: 'tous', statut: 'tous', activite: 'tous' })}
                  className="w-full px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  <i className="bi bi-arrow-counterclockwise mr-1"></i>
                  Réinitialiser
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions groupées */}
        {!chargementComplet && (
          <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="contacts-chargement-partiel">
            <i className="bi bi-arrow-repeat animate-spin"></i>
            Carnet complet en cours de chargement — la recherche ne porte encore que sur les
            premiers contacts.
          </div>
        )}
        {selectedContacts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-xl">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <i className="bi bi-check2-circle text-slate-400"></i>
              {selectedContacts.length} contact(s) sélectionné(s)
            </span>
            <div className="flex flex-wrap gap-2 ml-auto">
              <button
                onClick={handleBulkEmail}
                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                data-testid="bulk-email-btn"
              >
                <i className="bi bi-envelope"></i>
                Envoyer email
              </button>
              <button
                onClick={handleBulkPrint}
                disabled={bulkPrinting}
                aria-label="Imprimer"
                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                data-testid="bulk-print-btn"
              >
                {bulkPrinting ? <i className="bi bi-hourglass-split animate-spin"></i> : <i className="bi bi-printer"></i>}
                Imprimer
              </button>
              {showArchivedContacts ? (
                <button
                  onClick={() => {
                    if (window.confirm(`Désarchiver ${selectedContacts.length} contact(s) ?`)) {
                      bulkUnarchiveContacts && bulkUnarchiveContacts(selectedContacts);
                    }
                  }}
                  data-testid="bulk-unarchive-btn"
                  className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                >
                  <i className="bi bi-arrow-counterclockwise"></i>
                  Désarchiver
                </button>
              ) : (
                <button
                  onClick={handleBulkArchiveClick}
                  data-testid="bulk-archive-btn"
                  className="px-3 py-1.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
                >
                  <i className="bi bi-archive"></i>
                  Archiver
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm(`Supprimer définitivement ${selectedContacts.length} contact(s) ?`)) {
                    bulkDeleteContacts && bulkDeleteContacts(selectedContacts);
                  }
                }}
                data-testid="bulk-delete-btn"
                className="px-3 py-1.5 bg-white border border-slate-200 text-red-600 hover:bg-red-50 hover:border-red-200 rounded-lg text-sm font-medium transition flex items-center gap-1.5 shadow-sm"
              >
                <i className="bi bi-trash"></i>
                Supprimer
              </button>
              <button
                onClick={() => setSelectedContacts([])}
                className="px-3 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg text-sm font-medium transition"
                data-testid="bulk-cancel-btn"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tableau - Version Desktop SEULEMENT */}
      <div className="hidden xl:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left w-12">
                  <input 
                    type="checkbox"
                    checked={paginatedContacts.length > 0 && selectedContacts.length === paginatedContacts.length}
                    onChange={toggleSelectAll}
                    className="w-5 h-5 text-green-600 rounded border-slate-300 focus:ring-2 focus:ring-green-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left w-16"></th>
                <th 
                  onClick={() => handleSort('nom')}
                  className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition"
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Nom
                    {sortField === 'nom' && (
                      <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} text-green-600`}></i>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left">
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Email / Tél</div>
                </th>
                <th 
                  onClick={() => handleSort('ville')}
                  className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition"
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Ville
                    {sortField === 'ville' && (
                      <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} text-green-600`}></i>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left">
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Groupes</div>
                </th>
                <th 
                  onClick={() => handleSort('created_at')}
                  className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition"
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-600 uppercase tracking-wide">
                    Créé le
                    {sortField === 'created_at' && (
                      <i className={`bi bi-arrow-${sortOrder === 'asc' ? 'up' : 'down'} text-green-600`}></i>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-center">
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedContacts.map(contact => (
                <tr key={contact.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                  <td className="px-4 py-3">
                    <input 
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleContactSelection(contact.id)}
                      className="w-5 h-5 text-green-600 rounded border-slate-300 focus:ring-2 focus:ring-green-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className={`w-10 h-10 ${getAvatarGradient(contact)} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm overflow-hidden`}>
                      {contact.photo_url ? (
                        <img 
                          src={getMediaUrl(contact.photo_url)} 
                          alt={`${contact.prenom} ${contact.nom}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitials(contact)
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => viewContact ? viewContact(contact) : editContact && editContact(contact)}
                      className="text-left hover:underline w-full"
                    >
                      <div className="font-bold text-slate-800">
                        {contact.titre && <span className="font-bold mr-1">{contact.titre}</span>}
                        {contact.prenom} {contact.nom}
                        {getContactSociete(contact) && (
                          <span className="font-medium text-slate-500" data-testid="contact-row-societe">
                            {' '}— {getContactSociete(contact)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {contact.type_contact && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-100 text-slate-700">
                            {contact.type_contact}
                          </span>
                        )}
                      </div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {contact.emails?.[0]?.email && (
                      <a href={`mailto:${contact.emails[0].email}`} className="block text-xs text-cyan-600 hover:text-cyan-700 hover:underline mb-1 truncate">
                        {contact.emails[0].email}
                      </a>
                    )}
                    {contact.telephones?.[0]?.numero && (
                      <a href={`tel:${contact.telephones[0].numero}`} className="block text-xs text-green-600 hover:text-green-700 hover:underline truncate num">
                        {formatPhoneFR(contact.telephones[0].numero)}
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-800">
                      {contact.ville || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-slate-600">
                      {contact.groupes?.length > 0 ? contact.groupes.slice(0, 2).join(', ') : '-'}
                      {contact.groupes?.length > 2 && <span className="text-slate-400"> +{contact.groupes.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-slate-600 num">
                      {contact.created_at ? formatDateFR(contact.created_at) : '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => viewContact && viewContact(contact)}
                        className="w-8 h-8 bg-[#EFF6FF] hover:bg-blue-200 text-[#007BFF] rounded-lg flex items-center justify-center transition"
                        title="Voir (consultation)"
                        data-testid={`contact-view-${contact.id}`}
                      >
                        <i className="bi bi-eye text-sm"></i>
                      </button>
                      <button
                        onClick={() => editContact && editContact(contact)}
                        className="w-8 h-8 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg flex items-center justify-center transition shadow-sm"
                        title="Modifier"
                        data-testid={`contact-edit-${contact.id}`}
                      >
                        <i className="bi bi-pencil text-sm"></i>
                      </button>
                      <div className="relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === contact.id ? null : contact.id); }}
                          className="w-8 h-8 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg flex items-center justify-center transition"
                          title="Plus d'actions"
                          data-testid={`contact-more-${contact.id}`}
                        >
                          <i className="bi bi-three-dots-vertical text-sm"></i>
                        </button>
                        {activeDropdown === contact.id && (
                          <div
                            className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-xl z-[60] py-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => { setActiveDropdown(null); window.open(`/contacts/${contact.id}/fiche`, '_blank'); }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                              data-testid={`contact-pdf-${contact.id}`}
                            >
                              <i className="bi bi-printer text-slate-500"></i>Imprimer la fiche
                            </button>
                            {/* Bouton « Historique » retiré (démo-ready) : non implémenté. */}
                            <div className="border-t border-slate-100 my-1"></div>
                            <button
                              onClick={() => handleArchiveClick(contact)}
                              className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                              data-testid={`contact-archive-${contact.id}`}
                            >
                              <i className="bi bi-archive text-amber-500"></i>Archiver
                            </button>
                            <button
                              onClick={() => handleDeleteClick(contact)}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              data-testid={`contact-delete-${contact.id}`}
                            >
                              <i className="bi bi-trash"></i>Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between">
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
              <span className="text-sm text-slate-600">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedContacts.length)} sur {sortedContacts.length}
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value={10}>10 par page</option>
                <option value={20}>20 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <i className="bi bi-chevron-double-left"></i>
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <span className="px-4 py-1.5 text-sm font-semibold text-slate-700">
                Page {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <i className="bi bi-chevron-right"></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <i className="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Vue Mobile/Tablette - Cards */}
      <div className="xl:hidden space-y-3">
        {paginatedContacts.map(contact => (
          <div key={contact.id} className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="p-4">
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <input 
                  type="checkbox"
                  checked={selectedContacts.includes(contact.id)}
                  onChange={() => toggleContactSelection(contact.id)}
                  className="mt-1 w-5 h-5 text-green-600 rounded border-slate-300 focus:ring-2 focus:ring-green-500 cursor-pointer"
                />
                
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 ${getAvatarGradient(contact)} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm overflow-hidden`}>
                    {contact.photo_url ? (
                      <img 
                        src={getMediaUrl(contact.photo_url)} 
                        alt={`${contact.prenom} ${contact.nom}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(contact)
                    )}
                  </div>
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => viewContact ? viewContact(contact) : editContact && editContact(contact)}
                    className="text-left w-full"
                  >
                    <h3 className="font-bold text-slate-800 mb-1">
                      {contact.titre && <span className="font-bold mr-1">{contact.titre}</span>}
                      {contact.prenom} {contact.nom}
                      {getContactSociete(contact) && (
                        <span className="font-medium text-slate-500" data-testid="contact-card-societe">
                          {' '}— {getContactSociete(contact)}
                        </span>
                      )}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {contact.type_contact && (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-100 text-slate-700">
                          {contact.type_contact}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Coordonnées */}
                  <div className="space-y-1 mb-2">
                    {contact.emails?.[0]?.email && (
                      <a href={`mailto:${contact.emails[0].email}`} className="block text-xs text-cyan-600 hover:text-cyan-700 hover:underline truncate">
                        <i className="bi bi-envelope mr-1"></i>
                        {contact.emails[0].email}
                      </a>
                    )}
                    {contact.telephones?.[0]?.numero && (
                      <a href={`tel:${contact.telephones[0].numero}`} className="block text-xs text-green-600 hover:text-green-700 hover:underline num">
                        <i className="bi bi-telephone mr-1"></i>
                        {formatPhoneFR(contact.telephones[0].numero)}
                      </a>
                    )}
                    {contact.ville && (
                      <div className="text-xs text-slate-600">
                        <i className="bi bi-geo-alt mr-1"></i>
                        {contact.ville}
                      </div>
                    )}
                  </div>

                  {/* Groupes */}
                  {contact.groupes?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {contact.groupes.slice(0, 3).map((groupe, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-700">
                          {groupe}
                        </span>
                      ))}
                      {contact.groupes.length > 3 && (
                        <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-600">
                          +{contact.groupes.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Date */}
                  <div className="text-xs text-slate-500 num">
                    <i className="bi bi-calendar3 mr-1"></i>
                    Créé le {contact.created_at ? formatDateFR(contact.created_at) : '-'}
                  </div>
                </div>
              </div>

              {/* Actions — pattern aligné PropertiesListPage (2 actions + menu ⋮) */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <button 
                  onClick={() => viewContact && viewContact(contact)}
                  className="flex-1 bg-[#EFF6FF] hover:bg-blue-200 text-[#007BFF] py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition"
                  data-testid={`contact-view-m-${contact.id}`}
                >
                  <i className="bi bi-eye"></i>Voir
                </button>
                <button 
                  onClick={() => editContact && editContact(contact)}
                  className="flex-1 bg-[#0062CC] hover:bg-[#0056b3] text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition shadow-sm"
                  data-testid={`contact-edit-m-${contact.id}`}
                >
                  <i className="bi bi-pencil"></i>Modifier
                </button>
                <div className="relative">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === `m-${contact.id}` ? null : `m-${contact.id}`); }}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2.5 rounded-lg transition"
                    title="Plus d'actions"
                    data-testid={`contact-more-m-${contact.id}`}
                  >
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>
                  {activeDropdown === `m-${contact.id}` && (
                    <div
                      className="absolute right-0 bottom-full mb-1 w-48 bg-white border border-slate-200 rounded-lg shadow-xl z-[60] py-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => { setActiveDropdown(null); window.open(`/contacts/${contact.id}/fiche`, '_blank'); }}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        data-testid={`contact-pdf-m-${contact.id}`}
                      >
                        <i className="bi bi-printer text-slate-500"></i>Imprimer la fiche
                      </button>
                      {/* Bouton « Historique » retiré (démo-ready) — non implémenté. */}
                      <div className="border-t border-slate-100 my-1"></div>
                      {showArchivedContacts ? (
                        <button
                          onClick={() => {
                            setActiveDropdown(null);
                            if (window.confirm(`Désarchiver ${contact.prenom} ${contact.nom} ?`)) {
                              bulkUnarchiveContacts && bulkUnarchiveContacts([contact.id]);
                            }
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50 flex items-center gap-2"
                          data-testid={`contact-unarchive-m-${contact.id}`}
                        >
                          <i className="bi bi-arrow-counterclockwise text-emerald-500"></i>Désarchiver
                        </button>
                      ) : (
                        <button
                          onClick={() => handleArchiveClick(contact)}
                          className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                          data-testid={`contact-archive-m-${contact.id}`}
                        >
                          <i className="bi bi-archive text-amber-500"></i>Archiver
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteClick(contact)}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        data-testid={`contact-delete-m-${contact.id}`}
                      >
                        <i className="bi bi-trash"></i>Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Pagination Mobile */}
        {totalPages > 1 && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">
                  {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedContacts.length)} sur {sortedContacts.length}
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <span className="px-4 py-2 text-sm font-semibold text-slate-700">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ContactCascadeModal
        open={cascadeModal.open}
        mode={cascadeModal.mode}
        contact={cascadeModal.contact}
        linkedProperties={cascadeModal.linkedProperties}
        hasActiveSearches={cascadeModal.hasActiveSearches}
        isAcquereur={cascadeModal.isAcquereur}
        isProprietaire={cascadeModal.isProprietaire}
        onClose={() => setCascadeModal((m) => ({ ...m, open: false }))}
        onConfirmArchive={confirmCascadeArchive}
      />
      <ContactBulkEmailModal
        open={showBulkEmail}
        contactIds={selectedContacts}
        onClose={() => setShowBulkEmail(false)}
        onSent={() => { setSelectedContacts([]); }}
      />
      <ContactBulkArchiveModal
        open={bulkArchiveModal.open}
        items={bulkArchiveModal.items}
        totalSelected={selectedContacts.length}
        busy={bulkArchiveBusy}
        onClose={() => setBulkArchiveModal({ open: false, items: [] })}
        onArchiveAll={() => runBulkArchive(true)}
        onArchiveContactsOnly={() => runBulkArchive(false)}
      />
    </div>
  );
};

export default ContactList;
